import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { saveImage } from '@/utils/compat';
import {
  subscribePendingSave,
  setPendingSave,
  type PendingSavePhoto,
} from '@/utils/pendingSave';

/**
 * Global "your photo is ready" gate. Appears only when an automatic save was
 * refused by the platform (iOS share sheet outside a user gesture) so the
 * photo would otherwise be lost. One fresh tap on "Save photo" re-runs the
 * share from a context iOS honors. Mounted once in App.tsx; portaled to
 * document.body above every screen and drawer (z-[500]).
 */
const PendingSaveOverlay: React.FC = () => {
  const [pending, setPending] = useState<PendingSavePhoto | null>(null);

  useEffect(() => subscribePendingSave(setPending), []);

  if (!pending) return null;

  const handleSave = async (): Promise<void> => {
    const result = await saveImage(pending.src, pending.filename);
    if (result === 'needs-gesture') return; // sheet dismissed — keep the overlay
    setPendingSave(null);
  };

  const handleDiscard = (): void => {
    setPendingSave(null);
  };

  return createPortal(
    <div className="fixed inset-0 z-[500] bg-black/85 flex flex-col items-center justify-center gap-4 p-6">
      <span className="text-yellow-400 font-mono text-lg text-center">Your photo is ready</span>
      <button
        onClick={handleSave}
        className="px-8 py-4 bg-yellow-400 text-black rounded-xl font-bold text-lg"
      >
        Save photo
      </button>
      <button
        onClick={handleDiscard}
        className="text-yellow-400/60 hover:text-yellow-300 underline font-mono text-sm"
      >
        Discard
      </button>
    </div>,
    document.body
  );
};

export default PendingSaveOverlay;
